import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { StyledCellHeader } from '../../../../components/StyledCellHeader';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';
import AlertMessageDialog from '../../../../components/Dialogs/alertMessageDialog';

export default function AsignarPrestamosDialog({ open, onClose, onConfirm, rutaId }) {
  const [prestamos, setPrestamos] = useState([]);
  const [selectedPrestamos, setSelectedPrestamos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [notas, setNotas] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [messageAlert, setMessageAlert] = useState('');

  useEffect(() => {
    if (open) {
      fetchAvailablePrestamos();
    }
  }, [open]);

  const fetchAvailablePrestamos = async () => {
    setLoading(true);
    try {
      // First, get all active loans
      const prestamosResponse = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/prestamos/${localStorage.getItem('idcompany')}?estado=activo`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      
      if (prestamosResponse.ok) {
        const allPrestamos = await prestamosResponse.json();
        
        // Then get loans already assigned to this route
        const assignedResponse = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/rutas/${rutaId}/prestamos`, {
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
            'X-company-ID': localStorage.getItem('idcompany')
          }
        });
        
        let assignedLoanIds = [];
        if (assignedResponse.ok) {
          const assignedData = await assignedResponse.json();
          assignedLoanIds = assignedData.map(loan => loan.prestamo_original_id);
        }
        
        // Filter out already assigned loans
        const availablePrestamos = allPrestamos.filter(prestamo => 
          !assignedLoanIds.includes(prestamo.prestamo_original_id)
        );
        
        setPrestamos(availablePrestamos);
      } else {
        setMessageAlert('Error al cargar los préstamos disponibles');
        setOpenAlert(true);
      }
    } catch (error) {
      console.error('Error fetching prestamos:', error);
      setMessageAlert('Error al cargar los préstamos disponibles');
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPrestamo = (prestamoId) => {
    setSelectedPrestamos(prev => {
      if (prev.includes(prestamoId)) {
        return prev.filter(id => id !== prestamoId);
      } else {
        return [...prev, prestamoId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPrestamos.length === filteredPrestamos.length) {
      setSelectedPrestamos([]);
    } else {
      setSelectedPrestamos(filteredPrestamos.map(p => p.prestamo_original_id));
      
      
    }
  };

  const handleSubmit = async () => {
    if (selectedPrestamos.length === 0) {
      setMessageAlert('Selecciona al menos un préstamo para asignar');
      setOpenAlert(true);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/rutas/${rutaId}/asignar-prestamos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({
          prestamo_ids: selectedPrestamos,
          notas: notas,
          idcompany: localStorage.getItem('idcompany'),
          assigned_by: localStorage.getItem('id')
        })
      });

      if (response.ok) {
        const result = await response.json();
        setMessageAlert(result.message);
        setOpenAlert(true);
        onConfirm();
        onClose();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Error al asignar préstamos');
      }
    } catch (error) {
      setMessageAlert(error.message || 'Error al asignar préstamos');
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedPrestamos([]);
    setSearchTerm('');
    setStatusFilter('todos');
    setNotas('');
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-DO", { 
      style: "currency", 
      currency: "DOP" 
    }).format(amount);
  };

  const filteredPrestamos = prestamos.filter(prestamo => {
    const matchesSearch = 
      prestamo.cliente_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prestamo.loan_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prestamo.cliente_identificacion?.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'todos' || prestamo.estado_prestamo === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                backgroundColor: 'primary.main',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}
            >
              <AssignmentIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography variant="h6" fontWeight="bold">
              Asignar Préstamos a Ruta
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Divider sx={{ mb: 3 }} />
          {/* Filters */}
          <Box mb={3}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Buscar préstamos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              
              <Grid item xs={12} md={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Checkbox
                    checked={selectedPrestamos.length === filteredPrestamos.length && filteredPrestamos.length > 0}
                    indeterminate={selectedPrestamos.length > 0 && selectedPrestamos.length < filteredPrestamos.length}
                    onChange={handleSelectAll}
                  />
                  <Typography variant="body2">
                    Seleccionar todos ({selectedPrestamos.length}/{filteredPrestamos.length})
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Notas */}
          <Box mb={3}>
            <Typography variant="body1" fontWeight={600} mb={1}>
              Notas de Asignación
            </Typography>
            <TextField
              fullWidth
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Notas adicionales sobre la asignación..."
            />
          </Box>

          {/* Préstamos Table */}
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <StyledCellHeader sx={{ width: 50 }}>Seleccionar</StyledCellHeader>
                  <StyledCellHeader>Cliente</StyledCellHeader>
                  <StyledCellHeader>Préstamo</StyledCellHeader>
                  <StyledCellHeader>Monto</StyledCellHeader>
                  <StyledCellHeader>Estado</StyledCellHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredPrestamos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Box textAlign="center">
                        <Typography variant="h6" color="text.secondary" mb={1}>
                          {prestamos.length === 0 ? 'No hay préstamos disponibles' : 'Todos los préstamos ya están asignados a esta ruta'}
                        </Typography>
                        {prestamos.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No se encontraron préstamos activos para asignar
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Todos los préstamos activos ya han sido asignados a esta ruta
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPrestamos.map((prestamo) => (
                    <TableRow key={prestamo.prestamo_original_id} hover>
                      <TableCell>
                        <Checkbox
                          checked={selectedPrestamos.includes(prestamo.prestamo_original_id)}
                          onChange={() => handleSelectPrestamo(prestamo.prestamo_original_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {prestamo.cliente_nombre}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {prestamo.cliente_identificacion}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {prestamo.loan_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(prestamo.monto_aprobado)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={prestamo.estado_prestamo} 
                          color={prestamo.estado_prestamo === 'activo' ? 'success' : 'default'} 
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {selectedPrestamos.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>{selectedPrestamos.length}</strong> préstamo(s) seleccionado(s) para asignar
              </Typography>
            </Alert>
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button onClick={onClose} variant="outlined" sx={{ fontWeight: 'bold' }}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading || selectedPrestamos.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            color="primarybutton"
            sx={{ 
              fontWeight: 'bold'
            }}
          >
            {loading ? 'Asignando...' : `Asignar ${selectedPrestamos.length} Préstamo(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertMessageDialog 
        message={messageAlert} 
        title="" 
        severity="success" 
        color="success" 
        open={openAlert} 
        handleClose={() => setOpenAlert(false)}
      />
    </>
  );
}
